import { Program, Renderer } from "pixi.js";
import { Camera, Color, Material, Mesh3D, MeshShader, ShadowCastingLight } from "pixi3d/pixi7"
import { TransparentSRMShaderSource } from "./TransparentSRMShaderSource";

let vert = `#version VERSION
  
  #define FEATURES
  
  precision highp float;

  VERT_IN vec3 a_Position;

  uniform mat4 u_Model;
  uniform mat4 u_ViewProjection;
  uniform mat4 u_LightViewProjectionMatrix;

  VERT_OUT vec4 v_PositionLightSpace;

  void main() {
    vec4 pos = u_Model * vec4(a_Position, 1.0);
    v_PositionLightSpace = u_LightViewProjectionMatrix * pos;
    gl_Position = u_ViewProjection * u_Model * vec4(a_Position, 1.0);
  }`;

let frag = `#version VERSION

  #define FEATURES

  #if defined(WEBGL1) //&& defined(USE_TEX_LOD)
    #extension GL_EXT_shader_texture_lod : enable
  #endif

  #if defined(WEBGL1)
    #extension GL_OES_standard_derivatives : enable
  #endif

  #if defined(WEBGL1) && defined(USE_HDR)
    #extension GL_OES_texture_float : enable
    #extension GL_OES_texture_float_linear : enable
  #endif

  precision highp float;

  FRAG_IN vec4 v_PositionLightSpace;

  #ifdef WEBGL2
    out vec4 FRAG_COLOR;
  #endif

  uniform sampler2D u_ShadowSampler;
  uniform vec4 u_ShadowColor;

  float linstep(float low, float high, float v) {
    return clamp((v-low) / (high-low), 0.0, 1.0);
  }

  vec4 _texture(sampler2D sampler, vec2 coord)
  {
    #ifdef WEBGL2
      return texture(sampler, coord);
    #else
      return texture2D(sampler, coord);
    #endif
  }

  float getShadowContribution() {
    vec3 coords = v_PositionLightSpace.xyz / v_PositionLightSpace.w * 0.5 + 0.5;
    if (coords.z < 0.01 || coords.z > 0.99 || coords.x < 0.01 || coords.x > 0.99 || coords.y < 0.01 || coords.y > 0.99) {
        return 1.0;
    }
    vec2 moments = vec2(1.0) - _texture(u_ShadowSampler, coords.xy).xy;
    float p = step(coords.z, moments.x);
    float variance = max(moments.y - moments.x * moments.x, 0.00002);
    float d = coords.z - moments.x;
    float pMax = linstep(0.2, 1.0, variance / (variance + d*d));
    return min(max(p, pMax), 1.0);
  }

  void main() {
    float shadow = 1.0 - getShadowContribution();
    vec4 shadowColor = u_ShadowColor * vec4(shadow);
    float shadowAlpha = shadow * shadowColor.a;
    FRAG_COLOR = vec4(shadowColor.rgb * shadowAlpha, shadowAlpha);
  }`;

export class TransparentShadowReceiverMaterial extends Material {
    private shadowCastingLight: ShadowCastingLight;
    private shadowColor: Color;

    constructor(shadowCastingLight: ShadowCastingLight, shadowColor: Color = new Color(0, 0, 0, 0.25)) {
        super();
        this.shadowCastingLight = shadowCastingLight;
        this.shadowColor = shadowColor;
    }

    public updateUniforms(mesh: Mesh3D, shader: MeshShader): void {
        shader.uniforms.u_Model = mesh.worldTransform.array;
        shader.uniforms.u_ViewProjection = Camera.main.viewProjection.array;
        if (this.shadowCastingLight) {
            shader.uniforms.u_ShadowSampler = this.shadowCastingLight.shadowTexture;
            shader.uniforms.u_LightViewProjectionMatrix = this.shadowCastingLight.lightViewProjection;
            shader.uniforms.u_ShadowColor = this.shadowColor.rgba;
        }
    }

    public createShader(mesh: Mesh3D, renderer: Renderer): MeshShader | undefined {
        const features: string[] = [];
        if (renderer.context.webGLVersion === 1) {
            features.push("WEBGL1 1");
        }
        if (renderer.context.webGLVersion === 2) {
            features.push("WEBGL2 1");
        }
        return new MeshShader(Program.from(TransparentSRMShaderSource.build(vert, features, renderer), TransparentSRMShaderSource.build(frag, features, renderer)));
    }
}